import { MouseEvent } from 'react'

import { formatDate, isNilOrEmptyString } from '@/shared/lib'
import { Badge, Icon } from '@/shared/ui'

import { DeleteButton } from './delete-button'
import { NoContent } from './no-content'
import { History } from '../../model/search-history-store/history'
import { useSearchHistoryStore } from '../../model/search-history-store/store'
import { Item, List } from '../popup/common'

const DEFAULT_LOCATION_NAME = '모든 지역'

const HistoryBadges = ({ child }: { child: History['child'] }) => {
  if (child.length === 0) return null

  return (
    <ul className="flex gap-1">
      {child[0].type === 'badge' &&
        child.map(item => (
          <li key={item.name} className="flex">
            <Badge variant="solid" color={item.color}>
              {item.shortName}
            </Badge>
          </li>
        ))}
      {child[0].type === 'icon' &&
        child.map(item => (
          <li key={item.name} className="flex">
            <Icon name={item.name} size={18} className={item.color} />
          </li>
        ))}
    </ul>
  )
}

const DateRange = ({
  startDate,
  endDate,
}: {
  startDate: string | null
  endDate: string | null
}) => {
  if (isNilOrEmptyString(startDate) || isNilOrEmptyString(endDate)) return null

  return (
    <p className="typo-caption1 font-medium text-gray-500">
      {formatDate({ date: startDate, format: 'yyyy.MM.dd' })}~
      {formatDate({ date: endDate, format: 'yyyy.MM.dd' })}
    </p>
  )
}

type Props = {
  onItemClick: (history: History) => void
}

export const SearchHistoryList = ({ onItemClick }: Props) => {
  const searchHistory = useSearchHistoryStore(state => state.searchHistory)
  const checkSearchHistoryExist = useSearchHistoryStore(
    state => state.checkSearchHistoryExist,
  )
  const searchHistoryActions = useSearchHistoryStore(state => state.actions)
  const hasSearchHistory = checkSearchHistoryExist()

  const handleItemClick = (history: History) => () => {
    onItemClick(history)
  }

  const handleDeleteItem = (id: string) => (event: MouseEvent) => {
    event.stopPropagation()
    searchHistoryActions.deleteSearchHistory(id)
  }

  const handleDeleteAll = () => {
    searchHistoryActions.clearSearchHistory()
  }

  return (
    <List title="최근 검색어" className="relative w-[400px]">
      {hasSearchHistory && <DeleteButton onClick={handleDeleteAll} />}
      {(() => {
        if (!hasSearchHistory)
          return <NoContent>최근 검색어가 없습니다.</NoContent>

        return searchHistory.map(history => (
          <Item
            key={history.id}
            onClick={handleItemClick(history)}
            data-item-type="search-history"
          >
            <div className="space-y-1">
              <HistoryBadges child={history.child} />
              <p>{history.name || DEFAULT_LOCATION_NAME}</p>
              <DateRange
                startDate={history.startDate}
                endDate={history.endDate}
              />
            </div>
            <div role="button" onClick={handleDeleteItem(history.id)}>
              <Icon name="close" size={16} className="text-gray-500" />
            </div>
          </Item>
        ))
      })()}
    </List>
  )
}
